/**
 * #742 — LA NORMALIZACIÓN DE BÚSQUEDA. GEMELO DEL ÁRBOL WEB.
 *
 * Copia byte a byte del cuerpo de `mcp-server/src/utils/normalizacionBusqueda.js`
 * (todo menos estas cuatro líneas): CRA sólo compila lo que cuelga de `src/`,
 * así que este árbol no puede importar del otro. Una copia por ÁRBOL, y un test
 * que compara los bytes — el mismo que en #740 atrapó el cambio de una sola
 * palabra en un solo gemelo.
 *
 * El dueño lo pidió textual: *"ocupo que removamos cualquier sensibilidad a
 * tildes"*. Medido, la casa tenía la regla a medias y al revés: en el MISMO
 * catálogo, `query_inventory.search` normaliza tildes y `list_projects.search`
 * no — así que el modelo aprende la regla en una tool y pierde la obra en la
 * otra. Y en todos los casos **la ortografía correcta del español es la que
 * falla**, porque los datos están guardados sin tildes: `"remodelación"` da 0,
 * `"REMODELACION"` da 1.
 *
 * ## POR QUÉ ESTO VIVE EN LA BASE Y NO EN JS
 *
 * De los 34 call sites que interpolan texto humano en un filtro, **19 paginan o
 * cuentan en la base** (`.range()` / `count: 'exact'`). Mover el filtro a JS
 * dejando esa paginación arma la página con las filas equivocadas y un total que
 * cuenta el universo SIN filtrar — y el total **no tiene arreglo en JS salvo
 * traerse todas las filas**, que es justo lo que la paginación existe para
 * evitar. Así que en esos 19 el filtro se queda donde está y **lo que se vuelve
 * insensible a tildes es la COLUMNA**: una columna generada por tabla-columna,
 * con su índice. `.range()` y `count: 'exact'` siguen exactos sin tocarse, y
 * `contarOcultas()` de #686 sigue midiendo el mismo universo.
 *
 * ## POR QUÉ `translate()` Y NO `unaccent()`
 *
 * `unaccent()` es `STABLE`, no `IMMUTABLE`: no se puede usar en una columna
 * generada ni indexar sin un wrapper `IMMUTABLE`. Y sin el wrapper **no falla**
 * — hace seq scan, o sea que el problema vuelve disfrazado de lentitud, que es
 * la clase de defecto que nadie atribuye a su causa. `translate()` ya es
 * inmutable de fábrica: sin `CREATE EXTENSION` que aprobar en producción y sin
 * wrapper que alguien pueda borrar en un refactor.
 *
 * ## EL RIESGO QUE ESTE ARCHIVO EXISTE PARA CERRAR
 *
 * Hay DOS normalizadores: el de la base (`translate`) y el de JS, que normaliza
 * el término del usuario antes de mandarlo al filtro. **Si no producen
 * exactamente lo mismo, la búsqueda falla en silencio**: el término normaliza a
 * algo que la columna no contiene y vuelve `[]` — indistinguible de "no existe",
 * que es el modo de falla que #686/#740 vinieron a cerrar.
 *
 * Por eso el mapeo **no está escrito dos veces**. Está acá, y el SQL de la
 * migración se GENERA desde acá (`mcp-server/scripts/generar-sql-normalizacion.mjs`).
 * El test corre el generador y exige que el `.sql` commiteado coincida byte a
 * byte, igual que `docs:mcp:check` con la doc de tools. La divergencia no queda
 * *detectada*: queda **imposible**.
 *
 * ## EL ALFABETO VA COMPLETO DE UNA VEZ, Y NO ES PRECAUCIÓN VAGA
 *
 * Ampliar el mapeo después **no es editar una constante**: es reescribir la
 * columna generada en todas las filas de las 9 tablas, con su migración a
 * producción y su reindexado. Cuesta un orden de magnitud más que ponerlo hoy.
 * Y `clients.company_name` / `suppliers.name` son **nombres propios**: ahí
 * aparece cualquier cosa. Medido sobre la base local, hoy sólo hay `á é í ó` —
 * razón de más para no decidir el alfabeto con los datos de hoy.
 *
 * @module utils/normalizacionBusqueda
 */

/**
 * El mapeo, en las DOS direcciones y con las dos cadenas de IGUAL longitud.
 *
 * `translate(cadena, origen, destino)` empareja por POSICIÓN de carácter. Si las
 * longitudes no coinciden, Postgres **no da error: trunca en silencio** y los
 * caracteres sobrantes del origen se BORRAN en vez de mapearse. Por eso hay un
 * test que compara `[...ORIGEN].length` con `[...DESTINO].length`, y por eso
 * están escritas en bloques alineados en vez de en una línea larga.
 */
export const ACENTOS_ORIGEN = [
  'áéíóú', 'üñ', 'àèìòù', 'âêîôû', 'äëïö', 'çãõ',
  'ÁÉÍÓÚ', 'ÜÑ', 'ÀÈÌÒÙ', 'ÂÊÎÔÛ', 'ÄËÏÖ', 'ÇÃÕ',
].join('');

export const ACENTOS_DESTINO = [
  'aeiou', 'un', 'aeiou', 'aeiou', 'aeio', 'cao',
  'AEIOU', 'UN', 'AEIOU', 'AEIOU', 'AEIO', 'CAO',
].join('');

/** El mapa listo para usar, derivado de las dos cadenas — no una tercera copia. */
const MAPA = new Map(
  [...ACENTOS_ORIGEN].map((caracter, i) => [caracter, [...ACENTOS_DESTINO][i]])
);

/**
 * Normaliza un texto EXACTAMENTE como lo hace la columna generada.
 *
 * El orden importa y es el del SQL: primero `translate`, después `lower`. Al
 * revés daría lo mismo para este mapeo, pero "daría lo mismo" es una propiedad
 * que hay que volver a demostrar cada vez que alguien toque el alfabeto, y no
 * una que convenga apoyar.
 *
 * NO usa `normalize('NFD')` + strip de diacríticos a propósito, aunque sea el
 * idiom de JS: eso cubre TODO Unicode y `translate()` cubre lo que enumera el
 * alfabeto. Serían dos reglas distintas que coinciden en los casos probados y
 * divergen en el primero que no — exactamente el `[]` silencioso que este
 * módulo existe para impedir.
 */
export const normalizarTexto = (texto) => {
  if (texto === null || texto === undefined) return '';
  let salida = '';
  for (const caracter of String(texto)) salida += MAPA.get(caracter) ?? caracter;
  return salida.toLowerCase();
};

/**
 * El sufijo de las columnas generadas: `title` -> `title_norm`.
 * Una sola definición para que el SQL y los filtros no puedan discrepar.
 */
export const columnaNormalizada = (columna) => `${columna}_norm`;

/**
 * #739 — SACA LOS METACARACTERES DE POSTGREST. **Esto es la seguridad.**
 *
 * `,` `(` `)` no son texto dentro de un filtro `.or()`: son la sintaxis. Un
 * término que los traiga no se busca — **agrega ramas al WHERE**. Medido con
 * credencial de usuario real sobre 3 clientes visibles:
 *
 *   search: `zzz-no-existe-nada-zzz`                    -> 0 filas
 *   search: `zzz%,id.not.is.null,company_name.ilike.%zzz` -> **3 filas**
 *
 * El payload ingenuo (una coma pelada) NO inyecta: rompe el parser. El que sí
 * funciona **cierra el `ilike`, mete la rama y reabre otro `ilike`** para que
 * los `%` que siguen caigan dentro de términos bien formados. Por eso el
 * defecto no se descubre tipeando una coma, y por eso llevaba tiempo ahí.
 *
 * Va SEPARADA de `escaparComodinesLike` a propósito: ver el comentario de esa.
 */
export const escaparMetacaracteresPostgrest = (termino) =>
  String(termino ?? '').replace(/[,()]/g, '');

/**
 * ESCAPA LOS COMODINES DE LIKE. **Esto NO es seguridad: es un cambio de
 * conducta para el usuario**, y por eso no vive adentro de la función de
 * arriba.
 *
 * Hoy un `%` tipeado por el usuario actúa como comodín; con esto pasa a buscar
 * un `%` literal, que es lo correcto (el usuario escribió un carácter, no un
 * operador) pero es **distinto de lo que pasaba ayer**. Queda declarado como
 * cambio de conducta en #742.
 *
 * Si las dos fueran un solo `safe()`, el día que alguien reporte "antes podía
 * buscar con %" la respuesta iba a ser "no toques eso, es de seguridad", y el
 * cambio de conducta quedaría enterrado adentro de algo que nadie se anima a
 * mover. Dos funciones, dos motivos, dos conversaciones distintas.
 */
export const escaparComodinesLike = (termino) =>
  String(termino ?? '').replace(/[\\%_]/g, '\\$&');

/**
 * Lo que se le manda a un filtro de PostgREST: las dos cosas, en orden, sobre
 * el término YA normalizado. El orden importa — escapar comodines primero
 * agrega `\`, y sacar metacaracteres después no lo toca; al revés tampoco
 * rompe, pero este orden es el que fija el test.
 */
export const terminoParaFiltro = (termino) =>
  escaparComodinesLike(escaparMetacaracteresPostgrest(normalizarTexto(termino)));

/**
 * LA VARIANTE PARA TABLAS QUE TODAVÍA NO TIENEN COLUMNA `*_norm`.
 *
 * Sanea —las dos cosas de arriba— y **NO normaliza**. No es una comodidad: es
 * lo único correcto mientras la columna contra la que se compara siga cruda.
 *
 * Medido en el lote 1 sobre `project_types.label` = `"Eléctrico"`, revirtiendo
 * la columna a cruda pero dejando el término normalizado:
 *
 *   "electrico"  -> 0 filas
 *   "eléctrico"  -> 0 filas   <-- falla escribiéndolo BIEN
 *
 * O sea que normalizar un solo lado deja la búsqueda **peor que antes** para
 * todo dato acentuado, y del modo silencioso de siempre: `[]`, indistinguible
 * de "no existe". Media implementación es una regresión, no un avance parcial.
 *
 * Para que una tabla pase a `terminoParaFiltro`: se agrega a `COLUMNAS_BUSCADAS`
 * y se regenera la migración. Hasta que eso esté aplicado, va esta.
 */
export const terminoParaFiltroSinNormalizar = (termino) =>
  escaparComodinesLike(escaparMetacaracteresPostgrest(termino));

/**
 * Las columnas de texto humano que se buscan hoy, por tabla. Medido con
 * `temp/742/decision.mjs` sobre los 19 sitios que paginan en la base.
 *
 * Es la entrada del generador de SQL: agregar una columna acá y regenerar es
 * todo lo que hace falta para sumar una tabla al alcance.
 */
export const COLUMNAS_BUSCADAS = {
  clients: ['company_name', 'primary_contact_name'],
  inventory: ['name', 'description', 'category'],
  opportunities: ['title'],
  profiles: ['full_name', 'company_name'],
  project_types: ['label'],
  projects: ['title'],
  purchases: ['location'],
  suppliers: ['name', 'contact_name'],
  tasks: ['title', 'description'],

  // ── LAS QUE EL BARRIDO NO VIO, y por qué esta lista NO está cerrada ──────
  //
  // Las nueve de arriba salieron de un barrido de un momento: los sitios que
  // paginaban en la base el día que se midió. `expenses` entró después, con
  // #747, y **nadie decidió nada** para que quedara afuera — fue deriva, no
  // diseño. Buscándole el resto salieron tres más.
  //
  // Dejarlas afuera reintroduce exactamente el bug que abrió este issue: dos
  // búsquedas con el mismo nombre comportándose distinto en el mismo catálogo.
  // Medido como consecuencia visible: quien escriba "instalacion" encontraría
  // obras y tareas y NO gastos.
  //
  // LA REGLA, para que la próxima tabla no dependa de que alguien la note:
  // **toda columna de texto humano que se busque necesita su `*_norm`.** Los
  // códigos (`*_number`, `sku`, `slug`) y los emails no: no tienen tildes.
  expenses: ['vendor', 'notes'],
  rfqs: ['title'],
  tool_categories: ['label_es', 'label_en'],
  tools: ['name', 'brand', 'model', 'category'],

  // ── LO QUE UN BARRIDO POR SITIO NO PODIA VER (#737) ─────────────────────
  //
  // `inventory.category` guarda "Consumible · Electrico · Quimico" y `tools`
  // guarda "Herramienta electrica · Instrumento de medicion": son ETIQUETAS
  // HUMANAS, no enums. Y se filtran con `.eq` EXACTO, asi que no aparecian ni
  // en el barrido de `.or(`/`.ilike(` — hizo falta mirar por COLUMNA, no por
  // sitio. Es #737: `"eléctrico"` bien escrito devuelve 0 de 15 materiales.
  //
  // NO entran las que PARECEN iguales y son enums de verdad: `tasks.category`
  // (documentation · general · inspection…) y `expenses.category` (labor ·
  // materials · tools…) son slugs ASCII. Normalizarlos no compra nada y les
  // cambiaria la semantica a un valor que la UI elige de una lista.
};

/**
 * LAS OLAS DE MIGRACION, y por que existe esta estructura.
 *
 * `COLUMNAS_BUSCADAS` dice QUE esta normalizado. Esto dice CUANDO se creo cada
 * columna, y hace falta porque las migraciones de la ola 1 **ya estan
 * aplicadas**: agregarle una columna a la migracion de `inventory` cambiaria un
 * archivo ya corrido, que es la forma de drift que este repo documenta tres
 * veces. Una columna nueva sobre una tabla vieja necesita su PROPIA migracion.
 *
 * Hay un test que exige que la union de las olas sea EXACTAMENTE
 * `COLUMNAS_BUSCADAS`: asi no se puede normalizar una columna en el JS sin
 * darle su migracion, ni al reves.
 */
export const OLAS = [
  {
    nombre: 'ola1',
    columnas: {
      clients: ['company_name', 'primary_contact_name'],
      inventory: ['name', 'description'],
      opportunities: ['title'],
      profiles: ['full_name', 'company_name'],
      project_types: ['label'],
      projects: ['title'],
      purchases: ['location'],
      suppliers: ['name', 'contact_name'],
      tasks: ['title', 'description'],
      expenses: ['vendor', 'notes'],
      rfqs: ['title'],
      tool_categories: ['label_es', 'label_en'],
      tools: ['name', 'brand', 'model'],
    },
  },
  {
    nombre: 'ola2',
    columnas: {
      inventory: ['category'],
      tools: ['category'],
    },
  },
];
